import { motion } from 'framer-motion';
import { CheckCircle2, FileText } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useRegularProjects } from '../hooks/useProjects';
import { DashboardHeader } from '../components/DashboardHeader';
import { Markdown } from '../components/Markdown';
import { useNavigate } from 'react-router-dom';
import { getDateSlipFeed, getMessagingCoverage } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import type { Project } from '../types';
import { confidenceFromScore } from '../lib/deliveryConfidence';

function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00`).getTime();
  const to = new Date(`${toIso}T00:00:00`).getTime();
  return Math.round((to - from) / 86_400_000);
}

function statusOrder(status: string): number {
  switch (status) {
    case 'on_track': return 0;
    case 'active': return 1;
    case 'planning': return 2;
    case 'draft': return 3;
    default: return 4;
  }
}

const statusColors: Record<string, string> = {
  at_risk: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  on_track: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  active: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  planning: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  draft: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  complete: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400',
};

function ProjectSkeleton() {
  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="skeleton h-4 w-2/3 rounded" />
      <div className="skeleton h-3 w-1/3 rounded" />
    </div>
  );
}

function LaunchReadinessCard({ project }: { project: Project }) {
  const navigate = useNavigate();
  const pct = confidenceFromScore(project.overallRiskScore);
  const colorCls = statusColors[project.status] ?? statusColors.archived;
  // null = not yet evaluated: neutral, never 0% red.
  const confColor =
    pct === null
      ? 'text-gray-400 dark:text-gray-500'
      : pct >= 80
        ? 'text-green-600 dark:text-green-400'
        : pct >= 60
          ? 'text-yellow-600 dark:text-yellow-400'
          : 'text-red-600 dark:text-red-400';

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => navigate(`/projects/${project.projectId}`)}
      className="flex items-center justify-between gap-4 px-4 py-3 bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl cursor-pointer hover:shadow-sm transition-shadow"
    >
      <p className="text-title text-gray-900 dark:text-white truncate flex-1">
        {project.name}
      </p>
      <span className={`flex-shrink-0 text-meta font-semibold px-2 py-0.5 rounded-full ${colorCls}`}>
        {project.status?.replace('_', ' ')}
      </span>
      <span className={`flex-shrink-0 text-body font-bold ${confColor}`}>
        {pct !== null ? `${pct}%` : '—'}
      </span>
    </motion.div>
  );
}

function RecentlyCompletedCard({ project }: { project: Project }) {
  const navigate = useNavigate();

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => navigate(`/projects/${project.projectId}`)}
      className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 cursor-pointer hover:shadow-sm transition-shadow"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-title text-gray-900 dark:text-white leading-snug">
          {project.name}
        </p>
        <span className="flex-shrink-0 flex items-center gap-1 text-meta font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircle2 className="h-4 w-4" />
          Ready to announce
        </span>
      </div>
      {project.description && (
        <Markdown size="xs" className="text-gray-500 dark:text-gray-400 line-clamp-2">
          {project.description}
        </Markdown>
      )}
    </motion.div>
  );
}

function DateSlipPanel() {
  const { data: slips, isLoading } = useQuery({
    queryKey: queryKeys.dateSlips,
    queryFn: getDateSlipFeed,
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5"
    >
      <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
        Date-Slip Feed
      </h2>
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => <ProjectSkeleton key={i} />)}
        </div>
      ) : !slips || slips.length === 0 ? (
        <p className="text-body text-gray-500 dark:text-gray-400 max-w-2xl">
          No launch dates have moved yet. This fills in automatically the first
          time a project&apos;s target date changes.
        </p>
      ) : (
        <div className="space-y-4">
          {slips.map((slip, i) => (
            <div
              key={`${slip.projectId}-${slip.changedAt}-${i}`}
              className="flex items-center justify-between gap-3 px-3 py-2.5 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg"
            >
              <div className="min-w-0">
                <p className="text-meta font-medium text-gray-900 dark:text-white truncate">
                  {slip.projectName}
                </p>
                <p className="text-meta text-gray-500 dark:text-gray-400">
                  {formatShortDate(slip.previousDueDate)} &rarr; {formatShortDate(slip.newDueDate)}
                </p>
              </div>
              <span className="flex-shrink-0 text-meta font-bold text-red-600 dark:text-red-400">
                +{daysBetween(slip.previousDueDate, slip.newDueDate)}d
              </span>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function MessagingCoveragePanel() {
  const { data: coverage, isLoading } = useQuery({
    queryKey: queryKeys.messagingCoverage,
    queryFn: getMessagingCoverage,
  });

  const total = coverage?.length ?? 0;
  const covered = (coverage ?? []).filter((c) => c.hasMessagingAsset).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5"
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Messaging Asset Coverage
        </h2>
        {!isLoading && total > 0 && (
          <span className="text-meta font-semibold text-gray-500 dark:text-gray-400">
            {covered}/{total}
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <ProjectSkeleton key={i} />)}
        </div>
      ) : total === 0 ? (
        <p className="text-meta text-gray-500 dark:text-gray-400">No active projects.</p>
      ) : (
        <div className="space-y-1.5">
          {(coverage ?? []).map((entry) => (
            <div
              key={entry.projectId}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/[0.03]"
            >
              <span className="text-meta text-gray-900 dark:text-white truncate">{entry.projectName}</span>
              {entry.hasMessagingAsset ? (
                <span className="flex-shrink-0 flex items-center gap-1 text-meta font-semibold text-green-700 dark:text-green-400">
                  <FileText className="h-4 w-4" />
                  Has doc
                </span>
              ) : (
                <span className="flex-shrink-0 text-meta font-semibold text-gray-400 dark:text-gray-500">
                  Missing
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export function MarketingDashboard({ onBackToToday }: { onBackToToday?: () => void } = {}) {
  const { data: projects, isLoading } = useRegularProjects();

  const regularProjects = projects ?? [];

  const launchReady = [...regularProjects]
    .filter((p) => ['on_track', 'active', 'planning', 'draft'].includes(p.status))
    .sort((a, b) => statusOrder(a.status) - statusOrder(b.status));

  const recentlyCompleted = regularProjects.filter(
    (p) => p.status === 'complete'
  );

  return (
    <div className="px-6 py-6 sm:px-10 lg:px-16">
      <DashboardHeader title="Marketing View" roleLabel="Marketing" onBackToToday={onBackToToday} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Launch Readiness */}
        <div className="space-y-4">
          <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Launch Readiness
          </h2>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => <ProjectSkeleton key={i} />)}
            </div>
          ) : launchReady.length === 0 ? (
            <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5">
              <p className="text-meta text-gray-500">No active projects.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {launchReady.map((project: Project) => (
                <LaunchReadinessCard key={project.projectId} project={project} />
              ))}
            </div>
          )}
        </div>

        {/* Recently Completed */}
        <div className="space-y-4">
          <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Recently Completed
          </h2>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => <ProjectSkeleton key={i} />)}
            </div>
          ) : recentlyCompleted.length === 0 ? (
            <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5">
              <p className="text-meta text-gray-500">No completed projects yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentlyCompleted.map((project: Project) => (
                <RecentlyCompletedCard key={project.projectId} project={project} />
              ))}
            </div>
          )}
        </div>

        {/* Date-Slip Feed */}
        <div>
          <DateSlipPanel />
        </div>

        {/* Messaging Asset Coverage */}
        <div>
          <MessagingCoveragePanel />
        </div>
      </div>
    </div>
  );
}
